import { InvoiceStatus } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { INVOICING_PATH } from "../../modules/Invoicing";
import { WSQueries } from "../../query/WSQuery";
import {
  useInvoiceQuery,
  useInvoiceTemplateQuery
} from "../../query/payments/queries";
import { useQueryInvoiceCollaborators } from "../components/InvoiceCollaborators";
import { InvoicesForm } from "../components/InvoicesForm/InvoicesForm";
import { useDefaultInvoiceSubject } from "../utils";
import {
  attachmentsToFormData,
  convertToLineItemFormData,
  creditFeeToFormData,
  defaultFrequency,
  invoiceCollaboratorToFormData,
  lateFeeToFormData,
  paymentMethodsToFormData
} from "../utils/invoicesFormUtils";

type Props = RouteComponentProps<{ invoiceId: string }>;

export const InvoicesInvoiceEdit: React.FC<Props> = ({ match }) => {
  const history = useHistory();
  const invoiceId = match.params.invoiceId;
  const queryInvoice = useInvoiceQuery(invoiceId, {
    onSuccess: invoice => {
      const hasWorkItems = invoice.lineItems.some(
        lineItem => !!lineItem.labels.workItemId
      );

      if (hasWorkItems) {
        history.push(INVOICING_PATH + "/" + invoiceId + "/edit-work-log");
      }
    }
  });
  const queryInvoiceCollaborators = useQueryInvoiceCollaborators(
    invoiceId,
    queryInvoice.data?.collaborators,
    { enabled: !!queryInvoice.data }
  );
  const queryParentInvoiceTemplate = useInvoiceTemplateQuery(
    queryInvoice.data?.parentInvoiceId!,
    {
      retry: false,
      enabled: !!queryInvoice.data?.parentInvoiceId
    }
  );

  const defaultSubject = useDefaultInvoiceSubject();

  return (
    <WSQueries queries={{ queryInvoice, queryInvoiceCollaborators }}>
      {({
        queryInvoice: { data: invoice },
        queryInvoiceCollaborators: { data: invoiceCollaborators }
      }) => (
        <InvoicesForm
          context={{
            invoice,
            parentInvoiceTemplate: queryParentInvoiceTemplate.data
          }}
          defaultValues={{
            subject: String(invoice.labels?.subject || defaultSubject),
            client: {
              payerId: invoice.clientId || null,
              payerPayeeEngagementId:
                (invoice.memberClientId as string) || null,
              emailsCC: []
            },
            purchaseOrderNumber: invoice.metadata?.purchaseOrderNumber || "",
            lineItems: (invoice?.lineItems || []).map(lineItem =>
              convertToLineItemFormData(lineItem)
            ),
            other: {
              projectName: String(invoice?.labels?.projectName || ""),
              notes: invoice?.invoiceNotes || "",
              attachments: attachmentsToFormData(
                invoice?.attachments?.customAttachmentIds
              )
            },
            due: "custom",
            customDueDate: invoice?.dueDate || new Date(),
            includesLateFee:
              !!invoice.lateFeeHandling && !!invoice.lateFeeHandling?.frequency,
            lateFee: lateFeeToFormData(invoice.lateFeeHandling),
            send: {
              type: "immediately",
              date: new Date()
            },
            recurring: false,
            frequency: defaultFrequency,

            invoiceCollaborators:
              invoiceCollaboratorToFormData(invoiceCollaborators),
            paymentMethods: paymentMethodsToFormData(
              invoice.acceptedPaymentMethods
            ),
            advanced: {
              sendEmails: !!invoice?.notificationPreferences?.sendInvoice,
              sendReminders: !!invoice?.notificationPreferences?.sendReminders,
              creditFeeHandling: creditFeeToFormData(invoice),
              date:
                invoice?.metadata?.invoiceDate ||
                (invoice?.status === InvoiceStatus.Draft
                  ? new Date()
                  : invoice.createdAt) ||
                new Date()
            }
          }}
        />
      )}
    </WSQueries>
  );
};
